import React, { useCallback, useEffect, useState } from 'react';
import {
  Card,
  CardHeader,
  CardContent,
  PaginatedTable,
  Button,
  Badge,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui';
import type { PaginationState } from '../../components/ui';
import { PageHeader } from '@/components/layout/PageHeader';
import { adminBackofficeApi } from '../../services/adminBackoffice';
import { Loader2 } from 'lucide-react';

interface AccountingSyncAttemptRow {
  id: string;
  integrationId: string;
  academyId: string;
  academyName: string;
  remotePlatformName: string | null;
  stripeEventId: string;
  stripeEventType: string;
  syncOperation: string;
  status: string;
  attempts: number;
  remoteEntityId: string | null;
  nextRetryAt: string;
  createdAt: string;
  updatedAt: string;
}

function statusBadgeVariant(
  status: string
): 'default' | 'secondary' | 'destructive' | 'outline' {
  switch (status) {
    case 'succeeded':
      return 'default';
    case 'failed':
      return 'destructive';
    case 'pending':
      return 'secondary';
    default:
      return 'outline';
  }
}

export default function AccountingSyncAttemptsPage() {
  const [attempts, setAttempts] = useState<AccountingSyncAttemptRow[]>([]);
  const [pagination, setPagination] = useState<PaginationState | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [sortBy, setSortBy] = useState<string>('createdAt');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [resettingId, setResettingId] = useState<string | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailJson, setDetailJson] = useState<string | null>(null);
  const [detailTitle, setDetailTitle] = useState<string>('');

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    adminBackofficeApi
      .getAccountingSyncAttempts({
        page,
        limit: 20,
        sortBy,
        sortOrder,
        ...(statusFilter ? { status: statusFilter } : {}),
      })
      .then((data) => {
        if (data.success && data.data) {
          setAttempts(data.data.attempts ?? []);
          setPagination(data.data.pagination ?? null);
        } else setError(data.error || 'Failed to load');
      })
      .catch((err) => setError(err?.response?.data?.error || err?.message || 'Failed to load'))
      .finally(() => setLoading(false));
  }, [page, statusFilter, sortBy, sortOrder]);

  useEffect(() => {
    load();
  }, [load]);

  const handleResetRetry = async (id: string) => {
    setResettingId(id);
    setError(null);
    try {
      const data = await adminBackofficeApi.resetAccountingSyncAttempt(id);
      if (!data.success) {
        setError(data.error || 'Reset failed');
        return;
      }
      load();
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { error?: string } } })?.response?.data?.error ||
        (err as Error)?.message ||
        'Reset failed';
      setError(msg);
    } finally {
      setResettingId(null);
    }
  };

  const openDetail = async (id: string) => {
    setDetailTitle(id);
    setDetailOpen(true);
    setDetailLoading(true);
    setDetailJson(null);
    try {
      const data = await adminBackofficeApi.getAccountingSyncAttempt(id);
      if (data.success && data.data?.attempt) {
        setDetailJson(JSON.stringify(data.data.attempt, null, 2));
      } else {
        setDetailJson(JSON.stringify({ error: data.error || 'Failed to load' }, null, 2));
      }
    } catch (err: unknown) {
      setDetailJson(
        JSON.stringify({ error: (err as Error)?.message || 'Failed to load' }, null, 2)
      );
    } finally {
      setDetailLoading(false);
    }
  };

  if (error && attempts.length === 0 && !loading) {
    return (
      <div>
        <PageHeader
          title="Accounting sync queue"
          help={
            <p>
              Accounting outbox rows for Stripe events synced to Xero or QuickBooks. Failed rows can be
              reset after fixing credentials or mapping.
            </p>
          }
        />
        <div className="rounded-lg border border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-900/20 p-4 text-red-700 dark:text-red-300">
          {error}
        </div>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Accounting sync queue"
        help={
          <p>
            Accounting outbox: one row per Stripe event and sync operation. Pending rows are claimed by
            the scheduled worker; failed rows stop after max retries.
          </p>
        }
      />

      {error && attempts.length > 0 && (
        <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-900/20 p-3 text-sm text-amber-900 dark:text-amber-100">
          {error}
        </div>
      )}

      <Card>
        <CardHeader>
          <div className="flex flex-col gap-4">
            <h2 className="text-lg font-medium">Outbox attempts</h2>
            <div className="flex flex-col gap-3 lg:flex-row lg:flex-wrap lg:items-center lg:gap-4">
              <label className="flex items-center gap-2 text-sm">
                <span className="text-gray-600 dark:text-gray-400 whitespace-nowrap">Status</span>
                <select
                  className="rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-1.5 text-sm min-w-[10rem]"
                  value={statusFilter}
                  onChange={(e) => {
                    setStatusFilter(e.target.value);
                    setPage(1);
                  }}
                >
                  <option value="">All</option>
                  <option value="pending">Pending</option>
                  <option value="failed">Failed</option>
                  <option value="succeeded">Succeeded</option>
                </select>
              </label>
              <label className="flex items-center gap-2 text-sm">
                <span className="text-gray-600 dark:text-gray-400 whitespace-nowrap">Sort by</span>
                <select
                  className="rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-1.5 text-sm min-w-[11rem]"
                  value={sortBy}
                  onChange={(e) => {
                    setSortBy(e.target.value);
                    setPage(1);
                  }}
                >
                  <option value="createdAt">Created</option>
                  <option value="updatedAt">Updated</option>
                  <option value="nextRetryAt">Next retry</option>
                  <option value="stripeEventType">Stripe event type</option>
                  <option value="status">Status</option>
                  <option value="attempts">Attempts</option>
                  <option value="stripeEventId">Stripe event ID</option>
                  <option value="syncOperation">Sync operation</option>
                </select>
              </label>
              <label className="flex items-center gap-2 text-sm">
                <span className="text-gray-600 dark:text-gray-400 whitespace-nowrap">Order</span>
                <select
                  className="rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-1.5 text-sm min-w-[10rem]"
                  value={sortOrder}
                  onChange={(e) => {
                    setSortOrder(e.target.value as 'asc' | 'desc');
                    setPage(1);
                  }}
                >
                  <option value="desc">Descending</option>
                  <option value="asc">Ascending</option>
                </select>
              </label>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <PaginatedTable<AccountingSyncAttemptRow>
            columns={[
              {
                key: 'academy',
                header: 'Academy',
                render: (row) => (
                  <div className="text-xs">
                    <div className="font-medium text-gray-900 dark:text-white">{row.academyName}</div>
                    <div className="font-mono text-[10px] text-gray-500 break-all">{row.academyId}</div>
                  </div>
                ),
              },
              {
                key: 'platform',
                header: 'Books',
                render: (row) => (
                  <span className="text-xs">{row.remotePlatformName ?? '—'}</span>
                ),
              },
              {
                key: 'stripeType',
                header: 'Stripe event',
                render: (row) => (
                  <span className="font-mono text-xs break-all">{row.stripeEventType}</span>
                ),
              },
              {
                key: 'endpoint',
                header: 'Sync operation',
                render: (row) => (
                  <span className="font-mono text-xs break-all">{row.syncOperation}</span>
                ),
              },
              {
                key: 'status',
                header: 'Status',
                render: (row) => (
                  <Badge variant={statusBadgeVariant(row.status)}>{row.status}</Badge>
                ),
              },
              { key: 'attempts', header: 'Attempts', render: (row) => row.attempts },
              {
                key: 'nextRetry',
                header: 'Next retry',
                render: (row) =>
                  row.nextRetryAt ? new Date(row.nextRetryAt).toLocaleString() : '–',
              },
              {
                key: 'created',
                header: 'Created',
                render: (row) =>
                  row.createdAt ? new Date(row.createdAt).toLocaleString() : '–',
              },
              {
                key: 'actions',
                header: 'Actions',
                render: (row) => (
                  <div className="flex flex-wrap gap-2">
                    <Button type="button" variant="outline" size="sm" onClick={() => openDetail(row.id)}>
                      Details
                    </Button>
                    {row.status === 'failed' && (
                      <Button
                        type="button"
                        size="sm"
                        disabled={resettingId === row.id}
                        onClick={() => handleResetRetry(row.id)}
                      >
                        {resettingId === row.id ? (
                          <>
                            <Loader2 className="h-3 w-3 animate-spin mr-1 inline" />
                            Resetting
                          </>
                        ) : (
                          'Reset retry'
                        )}
                      </Button>
                    )}
                  </div>
                ),
              },
            ]}
            data={attempts}
            pagination={pagination}
            loading={loading}
            emptyMessage="No accounting sync attempts found."
            onPageChange={setPage}
            getRowKey={(row) => row.id}
          />
        </CardContent>
      </Card>

      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="font-mono text-sm truncate">{detailTitle}</DialogTitle>
          </DialogHeader>
          <div className="overflow-auto flex-1 min-h-0">
            {detailLoading ? (
              <div className="flex justify-center py-8">
                <Loader2 className="h-8 w-8 animate-spin text-gray-500" />
              </div>
            ) : (
              <pre className="text-xs bg-gray-50 dark:bg-gray-900 p-3 rounded-md overflow-auto max-h-[60vh] whitespace-pre-wrap break-words">
                {detailJson ?? '—'}
              </pre>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
